import type { ContainerDetailInput, ReleaseStatus } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { ContainerDetailFields } from './ContainerDetailFields';
import type { JobContainerDetails } from '../../../jobs.api';

interface DetailsFormValues {
  shippingLine?: string;
  vessel?: string;
  billOfLading?: string;
  releaseOrder?: string;
  releaseStatus?: ReleaseStatus;
  pickupZoneCode?: string;
  emptyReturnDepotZoneCode: string;
  emptyReturnFreeDaysDeadline: Dayjs;
  isTransit: boolean;
  isHazardous: boolean;
  bondReference?: string;
}

interface ContainerJobDetailsFormProps {
  details: JobContainerDetails | null;
  error: string | null;
  saving: boolean;
  onSave: (input: ContainerDetailInput) => Promise<boolean>;
}

function toFormValues(details: JobContainerDetails | null): Partial<DetailsFormValues> {
  if (!details) return { isTransit: false, isHazardous: false };
  return {
    shippingLine: details.shippingLine ?? undefined,
    vessel: details.vessel ?? undefined,
    billOfLading: details.billOfLading ?? undefined,
    releaseOrder: details.releaseOrder ?? undefined,
    releaseStatus: details.releaseStatus ?? undefined,
    pickupZoneCode: details.pickupZoneCode ?? undefined,
    emptyReturnDepotZoneCode: details.emptyReturnDepotZoneCode,
    emptyReturnFreeDaysDeadline: dayjs(details.emptyReturnFreeDaysDeadline),
    isTransit: details.isTransit,
    isHazardous: details.isHazardous,
    bondReference: details.bondReference ?? undefined,
  };
}

function toApiInput(values: DetailsFormValues): ContainerDetailInput {
  return {
    shippingLine: values.shippingLine || undefined,
    vessel: values.vessel || undefined,
    billOfLading: values.billOfLading || undefined,
    releaseOrder: values.releaseOrder || undefined,
    releaseStatus: values.releaseStatus,
    pickupZoneCode: values.pickupZoneCode,
    emptyReturnDepotZoneCode: values.emptyReturnDepotZoneCode,
    emptyReturnFreeDaysDeadline: values.emptyReturnFreeDaysDeadline.format('YYYY-MM-DD'),
    isTransit: values.isTransit,
    isHazardous: values.isHazardous,
    bondReference: values.bondReference || undefined,
  };
}

/**
 * Shipping, release and empty-return paperwork for a container job (PRD → Job intake → Captured
 * per job type (Container); gitaumoses4/chukua#31) — the empty-return depot and its free-days
 * deadline are mandatory, everything else fills in as it's learned, same as the job's own fields.
 */
export function ContainerJobDetailsForm({ details, error, saving, onSave }: ContainerJobDetailsFormProps) {
  const [form] = Form.useForm<DetailsFormValues>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(details));
  }, [details, form]);

  async function submit(values: DetailsFormValues) {
    await onSave(toApiInput(values));
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-sm font-semibold text-fg">Shipping & release</h2>
      <Form<DetailsFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={saving} initialValues={toFormValues(details)}>
        {error && <Alert className="mb-5" type="error" title={error} showIcon />}
        <ContainerDetailFields />
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save details
          </Button>
        </Form.Item>
      </Form>
    </section>
  );
}
